import { useNavigate } from 'react-router-dom'
import { FileText, BookOpen, FolderOpen, ExternalLink } from 'lucide-react'
import type { SearchResult } from '../../types'
import AuthorConvertButton from '../crm/AuthorConvertButton'

interface ResultCardProps {
  result: SearchResult
  projectId?: string
}

const SOURCE_BADGES: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  inventions: { label: 'Document', color: 'bg-iflow-50 text-iflow-700', icon: <FileText size={12} /> },
  shared: { label: 'Shared', color: 'bg-amber-50 text-amber-700', icon: <FolderOpen size={12} /> },
  journals: { label: 'Journal', color: 'bg-purple-50 text-purple-700', icon: <BookOpen size={12} /> },
}

// Shared by the click handler and the route chip so the two can never drift.
function getResultRoute(result: SearchResult): string | null {
  if (!result.id) return null
  if (result.source === 'journals') return `/journals/${result.id}`
  // BUG-103: patent search results routed to /document/{patent_id}, which
  // dead-ended ("Document not found"). Patents deep-link via /patents/{id}.
  if (result.source === 'patents') return `/patents/${result.id}`
  return `/document/${result.id}`
}

export default function ResultCard({ result, projectId }: ResultCardProps) {
  const navigate = useNavigate()
  const badge = SOURCE_BADGES[result.source] || { label: result.source, color: 'bg-sky-500/10 text-gray-600', icon: null }
  const route = getResultRoute(result)

  const handleClick = () => {
    if (route) navigate(route)
  }

  return (
    <div
      onClick={handleClick}
      className="light-card p-4 hover:border-iflow-300 hover:shadow-sm transition-all cursor-pointer"
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${badge.color}`}>
            {badge.icon} {badge.label}
          </span>
          <span className="text-xs text-gray-500">Score: {(result.score ?? 0).toFixed(2)}</span>
          {result.domain && (
            <span className="px-2 py-0.5 bg-purple-50 text-purple-600 text-xs font-medium rounded-full">
              {result.domain}
            </span>
          )}
          {/* CoWork Round 2 (#2780 follow-up): project-name chip so cross-
              project results from own-all/ecosystem scopes are obvious. */}
          {result.project_name && (
            <span
              data-testid="result-card-project-chip"
              className="px-2 py-0.5 bg-iflow-50 text-iflow-700 text-xs font-medium rounded-full"
              title={`Project: ${result.project_name}`}
            >
              {result.project_name}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {route && (
            <span
              data-testid="result-card-route-chip"
              className="text-[10px] font-mono text-gray-500 bg-gray-50 border border-gray-200 rounded px-1.5 py-0.5"
            >
              {route}
            </span>
          )}
          {result.doi && (
            <a
              href={`https://doi.org/${result.doi}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="p-1 text-gray-500 hover:text-iflow-600"
              title="Open DOI"
            >
              <ExternalLink size={13} />
            </a>
          )}
        </div>
      </div>
      <h4 className="text-sm font-medium text-gray-900 mb-1">{result.title}</h4>
      {result.summary && (
        <p className="text-xs text-gray-500 mb-1 line-clamp-2">{result.summary}</p>
      )}
      {result.authors && result.authors.length > 0 && (
        <div className="text-xs text-gray-500 mb-1 flex items-center gap-1 flex-wrap">
          {result.authors.slice(0, 3).map((author, idx) => (
            <span key={idx} className="inline-flex items-center">
              {idx > 0 && ', '}{author}
              <AuthorConvertButton authorString={author} sourceDocumentId={result.id}
                sourceDocumentTitle={result.title} sourceType={result.source === 'journals' ? 'journal' : 'paper'}
                projectId={projectId} />
            </span>
          ))}
          {result.authors.length > 3 && <span> +{result.authors.length - 3} more</span>}
        </div>
      )}
      {result.journal && (
        <p className="text-xs text-gray-500 italic">{result.journal}{result.year ? ` (${result.year})` : ''}</p>
      )}
      {result.topics && result.topics.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {result.topics.slice(0, 5).map((topic) => (
            <span key={topic} className="px-1.5 py-0.5 bg-sky-500/10 text-gray-500 text-xs rounded">
              {topic}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
